/**
 * Print Depot — hero slideshow
 *
 * Cross-fades the photographs behind the hero. The heading and buttons sit
 * outside the media layer and never move, so only the imagery changes.
 *
 * The outgoing slide is held at full opacity underneath while the incoming one
 * fades in over it, rather than the two cross-fading against each other. A
 * plain two-way fade dips through a washed-out midpoint where the scrim shows
 * through both — the same problem the category card slideshow hit.
 *
 * Cycling stops whenever it cannot be seen: reduced-motion preference, a
 * hidden tab, or the hero scrolled out of view.
 */
(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const started = new WeakSet();

  const setup = (media) => {
    if (started.has(media)) return;

    const slides = Array.from(media.querySelectorAll('.pd-hero__slide'));
    if (slides.length < 2) return;

    started.add(media);

    const interval = Math.max(2000, parseInt(media.dataset.pdHeroInterval, 10) || 6000);
    let current = slides.findIndex((s) => s.classList.contains('is-active'));
    if (current < 0) {
      current = 0;
      slides[0].classList.add('is-active');
    }

    let timer = null;
    let visible = true;

    const advance = () => {
      const previous = slides[current];
      current = (current + 1) % slides.length;
      const next = slides[current];

      previous.classList.remove('is-active');
      previous.classList.add('is-previous');
      next.classList.add('is-active');

      // Drop the held slide once the incoming one has finished arriving. Timing
      // off the transition itself keeps this in step with the CSS rather than
      // duplicating its duration here. The timeout is only a safety net for
      // when the transition never fires.
      const release = () => previous.classList.remove('is-previous');
      let done = false;
      const once = () => {
        if (done) return;
        done = true;
        release();
      };
      next.addEventListener('transitionend', once, { once: true });
      setTimeout(once, 3000);
    };

    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const play = () => {
      if (timer !== null || !visible || REDUCED.matches || document.hidden) return;
      timer = setInterval(advance, interval);
    };

    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : play()));
    REDUCED.addEventListener('change', () => (REDUCED.matches ? stop() : play()));

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            visible = entry.isIntersecting;
            if (visible) play();
            else stop();
          }
        },
        { threshold: 0 }
      ).observe(media);
    } else {
      play();
    }
  };

  const scan = () => {
    for (const media of document.querySelectorAll('[data-pd-hero-slides]')) setup(media);
  };

  scan();

  // Re-arm after theme editor edits, where the section is replaced wholesale.
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('DOMContentLoaded', scan);
})();
